// src/pages/admin/components/Content.js
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Grid, Card, CardContent, Typography, Avatar, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Alert, Tooltip, IconButton, Divider
} from '@mui/material';
import PeopleAltIcon       from '@mui/icons-material/PeopleAlt';
import MailOutlineIcon     from '@mui/icons-material/MailOutline';
import TouchAppIcon        from '@mui/icons-material/TouchApp';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import RefreshIcon         from '@mui/icons-material/Refresh';
import goobusinessImage    from '../../../assets/goobusiness.PNG';

const API_URL = process.env.REACT_APP_API_URL || '';

// ─── Farben je Status ────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  neu:               { color: '#1976d2', bg: '#e3f2fd', label: 'Neu' },
  Angefragt:         { color: '#ed6c02', bg: '#fff3e0', label: 'Angefragt' },
  'In Bearbeitung':  { color: '#9c27b0', bg: '#f3e5f5', label: 'In Bearbeitung' },
  Rückmeldung:       { color: '#0288d1', bg: '#e1f5fe', label: 'Rückmeldung' },
  Erfolgreich:       { color: '#2e7d32', bg: '#e8f5e9', label: 'Erfolgreich' },
  'Kein Verstoß':    { color: '#d32f2f', bg: '#ffebee', label: 'Kein Verstoß' },
};

const MUI_CHIP_COLOR = {
  neu:               'primary',
  Angefragt:         'warning',
  'In Bearbeitung':  'secondary',
  Rückmeldung:       'info',
  Erfolgreich:       'success',
  'Kein Verstoß':    'error',
};

// ─── KPI-Karte ───────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, color, loading }) {
  return (
    <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
        <Avatar sx={{ bgcolor: color + '22', width: 52, height: 52 }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
        </Avatar>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {label}
          </Typography>
          {loading
            ? <CircularProgress size={20} sx={{ mt: 0.5 }} />
            : <Typography variant="h5" fontWeight={700}>{value ?? '–'}</Typography>
          }
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Balken-Diagramm (pure MUI) ──────────────────────────────────────────────
function StatusChart({ data, loading }) {
  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  if (!data.length) return <Typography color="text.secondary">Keine Daten vorhanden.</Typography>;

  const max = Math.max(...data.map(d => d.anzahl), 1);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {data.map(({ status, anzahl }) => {
        const cfg     = STATUS_CONFIG[status] || { color: '#757575', bg: '#f5f5f5', label: status };
        const pct     = Math.round((anzahl / max) * 100);
        return (
          <Box key={status}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" fontWeight={500}>{cfg.label}</Typography>
              <Typography variant="body2" color="text.secondary">{anzahl}</Typography>
            </Box>
            <Box
              sx={{
                height: 12,
                borderRadius: 6,
                bgcolor: '#f0f0f0',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${pct}%`,
                  bgcolor: cfg.color,
                  borderRadius: 6,
                  transition: 'width 0.6s ease',
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Hauptkomponente ─────────────────────────────────────────────────────────
function Content() {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/stats`, {
        headers: getHeaders(),
      });
      setStats(data);
    } catch (err) {
      setError('Fehler beim Laden der Dashboard-Daten.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const kpis = stats?.kpis || {};
  const statusVerteilung = stats?.statusVerteilung || [];
  const letzteAnfragen   = stats?.letzteAnfragen   || [];

  const formatDate = d =>
    d ? new Date(d).toLocaleString('de-DE', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        }) : '–';

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>

      {/* ── Titel-Zeile ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={700}>Übersicht</Typography>
        <Tooltip title="Aktualisieren">
          <IconButton onClick={fetchStats} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── KPI-Karten ── */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<PeopleAltIcon />}
            label="Gesamte Anfragen"
            value={kpis.total_anfragen}
            color="#1976d2"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<MailOutlineIcon />}
            label="E-Mail ausstehend"
            value={kpis.email_ausstehend}
            color="#ed6c02"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<TouchAppIcon />}
            label="Token-Links geöffnet"
            value={kpis.token_geoeffnet}
            color="#9c27b0"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<CheckCircleIcon />}
            label="Erfolgreich entfernt"
            value={kpis.erfolgreich}
            color="#2e7d32"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* ── Statusverteilung + Letzte Anfragen ── */}
      <Grid container spacing={2}>

        {/* Statusverteilung */}
        <Grid item xs={12} md={4}>
          <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Bewertungen nach Status
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <StatusChart data={statusVerteilung} loading={loading} />
            </CardContent>
          </Card>
        </Grid>

        {/* Letzte Anfragen */}
        <Grid item xs={12} md={8}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Letzte Anfragen
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Unternehmen</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Anfrage am</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Bewertung</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>E-Mail</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Token</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {letzteAnfragen.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography color="text.secondary" variant="body2">
                              Keine Anfragen vorhanden.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : letzteAnfragen.map(row => (
                        <TableRow key={row.id} hover>
                          {/* Unternehmen */}
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar
                                src={
                                  row.bild_url?.includes('googleusercontent')
                                    ? row.bild_url
                                    : goobusinessImage
                                }
                                variant="rounded"
                                sx={{ width: 30, height: 30 }}
                              />
                              <Box>
                                <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 160 }}>
                                  {row.firmenname?.split(',')[0]}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                                  {row.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Datum */}
                          <TableCell>
                            <Typography variant="caption">
                              {formatDate(row.erstellungsdatum)}
                            </Typography>
                          </TableCell>

                          {/* Bewertung */}
                          <TableCell>
                            <Typography variant="body2">
                              {row.bewertung ? `${row.bewertung} ★` : '–'}
                              {row.bewertungen_gesamt ? (
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {' '}({row.bewertungen_gesamt})
                                </Typography>
                              ) : null}
                            </Typography>
                          </TableCell>

                          {/* E-Mail Status */}
                          <TableCell>
                            <Chip
                              label={row.email_versendet ? 'Gesendet' : 'Ausstehend'}
                              size="small"
                              color={row.email_versendet ? 'success' : 'default'}
                              variant={row.email_versendet ? 'filled' : 'outlined'}
                            />
                          </TableCell>

                          {/* Token Status */}
                          <TableCell>
                            {row.link_token ? (
                              <Chip
                                label={row.token_used ? 'Geöffnet' : 'Nicht geöffnet'}
                                size="small"
                                color={row.token_used ? 'primary' : 'default'}
                                variant={row.token_used ? 'filled' : 'outlined'}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">–</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
}

export default Content;